import { FORM_PAYER_TYPE, FORMULATE_DOCUMENTS_STATUSES } from '~/utils/dictionaries';
import {
	STEPS_LIST, STEPS_ID, STEPS, COMPANY_STEP, PAYMENT_STEP,
} from '~/utils/formWithSteps';

import { amountToNumber } from '~/utils/intl-converter';

const PRODUCT_ID_WITH_REQUIRED_VAT = 2;

export function state() {
	return {
		isAuth: false,
		currentStep: STEPS.LOGIN,
		redirectStep: null,
		isLoading: false,
		orderHash: '',
		isAutoBackStepEnabled: false, // Автоматическое определение шага Назад
		isAutoNextStepEnabled: false, // Автоматическое определение шага Вперёд
		isCompletedFieldsDisabled: false,
		isStepError: false,
		isVisiblePromocodeModal: false,
		currentProduct: {},
		personData: {},
		price: {
			initial: 0,
			withVat: 0,
			withPromocode: null,
			withLoyalty: null,
		},
		currentPromocode: {
			isActive: false,
			value: '',
		},
		form: {
			payer: {
				type: FORM_PAYER_TYPE.PHYSICAL,
			},
			documents: {
				snils_document: {},
				passport_document: {},
				education_document: {},
			},
		},
		order: {
			data: {
				products: [],
			},
		},
		customSteps: null,
		currentTab: null,
	};
}

export const mutations = {
	setForm(state, stepData) {
		state.form = { ...state.form, ...stepData };
	},
	setStep(state, step) {
		state.currentStep = step;
		state.redirectStep = null;
		state.isStepError = false;
	},
	setCustomSteps(state, steps) {
		state.customSteps = steps;
	},
	setLoading(state, value) {
		state.isLoading = value;
	},
	setPayerType(state, value) {
		state.form.payer = { ...state.form.payer, type: value };
	},
	setPrice(state, payload) {
		if (payload.value === null) {
			state.price[payload.key] = null;
			return;
		}
		state.price[payload.key] = amountToNumber(payload.value);
	},
	setRedirectStep(state, value) {
		state.redirectStep = value;
	},
	setOrderHash(state, value) {
		state.orderHash = value;
	},
	setProduct(state, value) {
		state.currentProduct = value;
	},
	setFieldsDisabled(state, value) {
		state.isCompletedFieldsDisabled = value;
	},
	setPersonData(state, value) {
		state.personData = value;
	},
	setStepError(state, value) {
		state.isStepError = value;
	},
	setPromocode(state, value) {
		state.currentPromocode = { ...state.currentPromocode, ...value };
	},
	setVisibilityPromocodeModal(state, value) {
		state.isVisiblePromocodeModal = value;
	},
	setIsAutoBackStepEnabled(state, value) {
		state.isAutoBackStepEnabled = value;
	},
	setIsAutoNextStepEnabled(state, value) {
		state.isAutoNextStepEnabled = value;
	},
	setCurrentTab(state, value) {
		state.currentTab = value;
	},
};

export const getters = {
	baseForm: (state) => state.form,
	payerType: (state) => state.form.payer.type,
	isPhysicalPayer(state, getters) {
		return getters.payerType === FORM_PAYER_TYPE.PHYSICAL;
	},
	isIpPayer(state, getters) {
		return getters.payerType === FORM_PAYER_TYPE.IP;
	},
	isRepresentative(state) {
		return state.form?.is_representative || state.personData?.is_representative;
	},
	isHideDocumentsStep(state, getters) {
		const { isRepresentative } = getters;
		return isRepresentative;
	},
	isNoEducation(state) {
		const {
			no_education: noEducation,
			documents: { education_document: educationDocument },
		} = state.form;
		const studentsNoEduction = state.form.students?.some((student) => +student.no_education);

		const generalNoEducation = Number((
			noEducation
			|| educationDocument?.no_education
			|| state.personData.no_education
			|| studentsNoEduction
		) || 0);

		// Если есть пользовательское значение, а не сохраненное - возвращаем его
		if (state.orderHash) return Number(noEducation ?? generalNoEducation);

		return generalNoEducation;
	},
	baseSteps(state) {
		return state.customSteps || STEPS_LIST;
	},
	stepsList(state, getters) {
		const { isHideDocumentsStep, isPhysicalPayer } = getters;

		let resultSteps;
		if (state.customSteps) {
			resultSteps = state.customSteps;
		} else {
			const currentSteps = isPhysicalPayer
				? [...getters.baseSteps, PAYMENT_STEP(isPhysicalPayer)]
				: [...getters.baseSteps, COMPANY_STEP, PAYMENT_STEP(isPhysicalPayer)];

			resultSteps = currentSteps
				.filter((step) => (isHideDocumentsStep ? step.name !== STEPS.DOCUMENTS : true));
		}

		return resultSteps.map((step, index, array) => {
			const isStepBlocked = !state.orderHash && step.id !== STEPS_ID.PAYER;
			const isPayerStep = step.id === STEPS_ID.PAYER || step.id === STEPS_ID.PAYER_JUNIORS;

			const wasPayerStep = isPayerStep && ![STEPS.LOGIN, STEPS.PAYER, STEPS.PAYER_JUNIORS].includes(state.currentStep);
			const currentStepIndex = array.findIndex((step) => step.id === state.personData.form_step);
			const isStepCompleted = wasPayerStep || (currentStepIndex !== -1 && index < currentStepIndex);

			return {
				...step,
				completed: isStepCompleted,
				blocked: isStepBlocked,
			};
		});
	},
	previousStep(state, getters) {
		const currentIndex = getters.stepsList.findIndex((step) => step.name === state.currentStep);

		// not found or first step
		if (currentIndex === -1 || currentIndex === 0) {
			return null;
		}

		for (let i = currentIndex - 1; i >= 0; i--) {
			if (!getters.stepsList[i].blocked) {
				return getters.stepsList[i].name;
			}
		}

		return null;
	},
	nextStep(state, getters) {
		const currentIndex = getters.stepsList.findIndex((step) => step.name === state.currentStep);

		// not found or last step
		if (currentIndex === -1 || currentIndex === getters.stepsList.length - 1) {
			return null;
		}

		return getters.stepsList[currentIndex + 1].name;
	},
	initialPrice(state, getters) {
		return getters.isNoEducation ? state.price.withVat : state.currentProduct?.price;
	},
	totalPrice(state, getters) {
		if (state.price.withPromocode === 0 || state.price.withPromocode > 0) {
			return state.price.withPromocode;
		}

		const { isNoEducation, isRepresentative, isRequiredVat } = getters;
		const { initial, withVat } = state.price;

		if (state.orderHash) {
			return isNoEducation ? withVat : initial;
		}

		let {
			price: currentProductPrice,
			program_price_with_vat: programPriceWithVat,
		} = state.currentProduct;
		if (currentProductPrice) {
			currentProductPrice = amountToNumber(currentProductPrice);
			programPriceWithVat = amountToNumber(programPriceWithVat);
		}

		const dynamicPrice = +initial || +currentProductPrice;
		const priceWithVat = +programPriceWithVat || +withVat;

		if (state.form.students?.length) {
			// eslint-disable-next-line no-nested-ternary
			const startAccPrice = isRepresentative
				? 0
				: (state.form.no_education ? priceWithVat : dynamicPrice);

			return state.form.students.reduce((acc, student) => {
				const price = +student.no_education ? priceWithVat : +dynamicPrice;

				return acc + price;
			}, startAccPrice);
		}

		if (isNoEducation || isRequiredVat) return priceWithVat;

		return dynamicPrice || 0;
	},
	currentRedirectStep(state) {
		return state.redirectStep;
	},
	isDocumentsVerified(state) {
		return Object.values(state.form.documents)
			.every((doc) => doc.verification_status === FORMULATE_DOCUMENTS_STATUSES.ACCEPTED);
	},
	isFreePromocode(state) {
		return state.price.withPromocode === 0;
	},
	isFreeLoyalty(state) {
		return state.price.withLoyalty === 0;
	},
	isFreeOrder(state, getters) {
		return getters.isFreePromocode || getters.isFreeLoyalty;
	},
	isRequiredVat(state) {
		return state.currentProduct?.vat === PRODUCT_ID_WITH_REQUIRED_VAT;
	},
};

export const actions = {
	async getPersonData({ commit, getters, state }, scenarioId) {
		const isPayerStep = state.currentStep === STEPS.PAYER
			|| state.currentStep === STEPS.PAYER_JUNIORS;
		if (isPayerStep && state.orderHash) return;
		try {
			commit('setLoading', true);
			const { data } = await this.$api.getPersonOnPayment(scenarioId);
			const {
				form_step: stepId,
				order_hash: hash,
				payer_type: payerType,
			} = data;

			commit('setPersonData', data);
			if (stepId === STEPS_ID.FINISH) {
				commit('setStep', STEPS.FINISH);
				commit('setOrderHash', hash);
				return;
			}
			if (state.currentStep === STEPS.FINISH) return;
			if (payerType) commit('setPayerType', payerType);
			commit('setOrderHash', hash);

			const currentStepName = getters.stepsList.find((step) => step.id === stepId)?.name;

			if (currentStepName) {
				commit('setStep', currentStepName);
			} else {
				// commit('setStep', 'info');
				// commit('setStep', STEPS.PAYER);
			}
		} catch (e) {
			console.error(e);
		} finally {
			commit('setLoading', false);
		}
	},
	async setCurrentStep({ commit, state, getters }, newStep) {
		commit('setStepError', false);
		try {
			const { currentStep } = state;
			const isResetStep = newStep === STEPS.RESET;
			const isFinishStep = newStep === STEPS.FINISH;
			const stepId = getters.stepsList.find((step) => step.name === newStep)?.id;
			const isPrevCompanyStep = currentStep === STEPS.COMPANY;
			const isSkip = [STEPS.PAYER, STEPS.INFO_JUNIORS].includes(newStep);

			commit('setStep', newStep);
			if (!isSkip && (stepId > state.personData.form_step || isPrevCompanyStep)) {
				await this.$api.updateFormStep(state.orderHash, stepId);
			}
			if (isResetStep) {
				commit('setStep', STEPS.PAYER);
				await this.$api.updateFormStep(state.orderHash, STEPS_ID.RESET);
			}
			if (isFinishStep) {
				commit('setStep', STEPS.FINISH);
				await this.$api.updateFormStep(state.orderHash, STEPS_ID.FINISH);
			}
			if (currentStep === STEPS.ADMISSION && newStep === STEPS.PAYMENT) {
				commit('setStep', STEPS.PAYMENT);
				await this.$api.updateFormStep(state.orderHash, STEPS_ID.PAYMENT);
			}
		} catch (e) {
			console.error(e);
		}
	},
	async resetForm({ commit, state }) {
		try {
			await this.$api.resetForm(state.orderHash);
			commit('setPersonData', {});
			commit('setOrderHash', '');
			commit('setStep', STEPS.PAYER);
		} catch (e) {
			console.error(e);
		}
	},
	async resetPromocode({ commit, state }) {
		try {
			await this.$api.resetForm(state.orderHash);
			commit('setPersonData', {});
			commit('setOrderHash', '');
			commit('setStep', 'info');
		} catch (e) {
			console.error(e);
		}
	},
};
